import { ChangeDetectionStrategy, Component, computed, input, output } from "@angular/core";
import { S25ContactProfileImageComponent } from "../../student-engagement/contact/contact.profile.image.component";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { approvalStatusDisplay, DiagramPersonRow, RESTRICTED_CONTACT_REASON } from "./workflow.diagram.model";

let nextPersonId = 0;

/**
 * One named person in the workflow diagram (on a node or in the detail pane), with a trailing
 * "Chat with {name}" button when chat is enabled. Unavailable chat keeps the button, marked aria-disabled,
 * with the reason as its description; restricted contacts get no button.
 */
@Component({
    selector: "workflow-diagram-person",
    template: `
        @let row = person();
        @if (!chatOnly()) {
            @if (row.restricted) {
                <span class="avatar-placeholder" [style.width.px]="avatarSize()" [style.height.px]="avatarSize()">
                    <s25-ng-icon [type]="'user'" [ariaLabel]="false" />
                </span>
            } @else {
                <s25-ng-contact-profile-image [contactId]="row.contId" [name]="row.name" [size]="avatarSize()" />
            }
            <div class="person-info">
                <div class="person-name">{{ row.name }}</div>
                @if (row.restricted) {
                    <span class="visuallyhidden">{{ restrictedReason }}</span>
                }
                @if (row.role) {
                    <div class="person-role">{{ row.role }}</div>
                }
                @if (showStatus()) {
                    <div class="person-status" [attr.data-tone]="status().tone">
                        <s25-ng-icon [type]="status().icon" [ariaLabel]="false" />
                        <span>{{ status().label }}</span>
                    </div>
                }
            </div>
        }
        @if (chatEnabled() && !row.restricted) {
            @if (row.chattable) {
                <button
                    type="button"
                    class="chat-button"
                    [attr.aria-label]="'Chat with ' + row.name"
                    (click)="chat.emit($any($event.currentTarget))"
                >
                    <s25-ng-icon [type]="'chat'" [ariaLabel]="false" />
                </button>
            } @else {
                <button
                    type="button"
                    class="chat-button unavailable"
                    aria-disabled="true"
                    [attr.aria-label]="'Chat with ' + row.name"
                    [attr.aria-describedby]="reasonId"
                    [attr.title]="unavailableReason()"
                >
                    <s25-ng-icon [type]="'chat'" [ariaLabel]="false" />
                </button>
                <span class="visuallyhidden" [id]="reasonId">{{ unavailableReason() }}</span>
            }
        }
    `,
    host: {
        class: "workflow-diagram-person",
        "[class.on-dark]": "onDark()",
    },
    styles: `
        :host {
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;
            box-sizing: border-box;
        }

        s25-ng-contact-profile-image,
        .avatar-placeholder {
            flex-shrink: 0;
        }

        .avatar-placeholder {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: var(--color--surface-2-bg);
            color: var(--color--text-muted);
        }

        .person-info {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 1px;
        }

        .person-name {
            font-size: 13px;
            font-weight: 500;
            line-height: 1.2;
            color: var(--color--text-strong);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        :host(:not(.on-dark)) .person-name {
            white-space: normal;
            overflow-wrap: anywhere;
        }

        .person-role,
        .person-status {
            font-size: 11px;
            line-height: 1.2;
            color: var(--color--text-muted);
        }

        .person-status {
            display: flex;
            align-items: center;
            gap: 3px;
            font-weight: 600;
            white-space: nowrap;
        }

        .person-status s25-ng-icon {
            --icon-size: 14px;
        }

        .person-status[data-tone="approved"] s25-ng-icon {
            color: var(--color--text-success);
        }
        .person-status[data-tone="denied"] s25-ng-icon {
            color: var(--color--text-danger);
        }
        .person-status[data-tone="changes"] s25-ng-icon {
            color: var(--color--text-warning);
        }

        .chat-button {
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 24px;
            min-height: 24px;
            padding: 3px;
            border: 1px solid var(--color--border);
            border-radius: 6px;
            background: var(--color--surface-bg);
            color: var(--color--text);
            cursor: pointer;
        }

        .chat-button s25-ng-icon {
            --icon-size: 16px;
        }

        .chat-button.unavailable {
            cursor: not-allowed;
            opacity: 0.6;
            border-style: dashed;
        }

        .chat-button:focus-visible {
            outline: 2px solid var(--color--text-strong);
            outline-offset: 2px;
        }

        :host(.on-dark) .person-name {
            color: #fff;
        }

        :host(.on-dark) .person-role,
        :host(.on-dark) .person-status {
            color: rgba(255, 255, 255, 0.92);
        }

        :host(.on-dark) .person-status[data-tone="approved"] s25-ng-icon {
            color: #4ade80;
        }
        :host(.on-dark) .person-status[data-tone="denied"] s25-ng-icon {
            color: #fecaca;
        }
        :host(.on-dark) .person-status[data-tone="changes"] s25-ng-icon {
            color: #fde68a;
        }

        :host(.on-dark) .chat-button {
            background: rgba(255, 255, 255, 0.15);
            border-color: rgba(255, 255, 255, 0.6);
            color: #fff;
        }

        :host(.on-dark) .chat-button:focus-visible {
            outline-color: #fff;
        }

        :host(.on-dark) .avatar-placeholder {
            background: rgba(255, 255, 255, 0.25);
            color: #fff;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25ContactProfileImageComponent, S25IconComponent],
})
export class WorkflowDiagramPersonComponent {
    person = input.required<DiagramPersonRow>();
    chatEnabled = input(false);
    showStatus = input(false);
    onDark = input(false);
    avatarSize = input(36);
    /** Render only the chat button (the row's name and avatar are shown elsewhere). */
    chatOnly = input(false);

    chat = output<HTMLElement>();

    protected readonly restrictedReason = RESTRICTED_CONTACT_REASON;
    protected readonly reasonId = `workflow-diagram-person-reason-${nextPersonId++}`;
    protected readonly status = computed(() => approvalStatusDisplay(this.person().status));
    protected readonly unavailableReason = computed(() => this.person().unavailableReason || "Chat unavailable");
}
